import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SortableTable from "app/components/SortableTable";
import Select from "react-select";
import { getDropdownFromRecord } from "app/containers/WeeklyCheckInMonitor/helpers";
import Button from "app/storybookComponents/Button";
import { useEffect, useMemo, useState } from "react";
import { Card, Form, Nav } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getEmailDeliverabilityInformation,
  getPeopleWithDeliverabilityIssues,
  selectAllEmailDeliverabilityInformation,
  selectGetEmailDeliverabilityInformationStatus,
  selectGetPeopleWithDeliverabilityIssuesStatus,
  selectPeopleWithDeliverabilityIssues,
} from "../slice";
import { RowContent } from "app/components/SortableTable/types";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { getSelectProps } from "utils/helperFunctions";
import { useDebounceCallback } from "utils/hooks";
import { Link } from "react-router-dom";
import { EMAIL_TYPE_TO_LABEL_MAP } from "../constants";
import { onFormatLargeNumber } from "../helpers";

interface Props {
  backButton?: {
    label?: string;
    onClick: () => void;
  };
  onTabChange?: (tab: string) => void;
  tabShowing?: string;
}

const EmailCheckInDeliverabilityCard = ({
  backButton,
  onTabChange,
  tabShowing,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const allEmailDeliverabilityInformation = useAppSelector(
    selectAllEmailDeliverabilityInformation
  );
  const peopleWithDeliverabilityIssues = useAppSelector(
    selectPeopleWithDeliverabilityIssues
  );
  const getEmailDeliverabilityInformationStatus = useAppSelector(
    selectGetEmailDeliverabilityInformationStatus
  );
  const getPeopleWithDeliverabilityIssuesStatus = useAppSelector(
    selectGetPeopleWithDeliverabilityIssuesStatus
  );

  const [timeInterval, setTimeInterval] = useState<string>("past30Days");
  const [displayedInput, setDisplayedInput] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  useEffect(() => {
    dispatch(getEmailDeliverabilityInformation({ timeInterval }));
  }, [dispatch, timeInterval]);

  useEffect(() => {
    dispatch(getPeopleWithDeliverabilityIssues());
  }, [dispatch]);

  const filteredPeopleWithDeliverabilityIssues = useMemo(
    () =>
      peopleWithDeliverabilityIssues.filter((peopleWithDeliverabilityIssue) => {
        const { userAccountId } = peopleWithDeliverabilityIssue;
        const user = usersById[userAccountId];
        const { firstName = "", lastName = "" } = user ?? {};
        const name = `${firstName} ${lastName}`;
        return name.toLowerCase().includes(searchInput.toLowerCase());
      }),
    [peopleWithDeliverabilityIssues, searchInput, usersById]
  );

  // If backButton is provided, render it otherwise no need to show back button
  const getBackButton = () => {
    if (!backButton) {
      return null;
    }
    const { label = "Back", onClick } = backButton;
    return (
      <div className="mb-3">
        <Button variant="secondary-blue" onClick={onClick}>
          <FontAwesomeIcon icon={"arrow-left"} className="me-2" />
          {label}
        </Button>
      </div>
    );
  };

  const onSearchInputChange = useDebounceCallback((value: string) => {
    setSearchInput(value);
  }, 500);

  const getEmailDeliverabilityTable = () => {
    const rows = getEmailDeliverabilityTableRows();
    return (
      <>
        <SortableTable
          rows={rows}
          hidePagination
          isLoading={getEmailDeliverabilityInformationStatus === "loading"}
          itemsPerPage={1000}
          columnHeaders={[
            {
              label: "Email Type",
              key: "emailType",
            },
            {
              label: "Total Emails Sent",
              key: "totalEmailsSent",
            },
            {
              label: "Delivered",
              key: "delivered",
            },
            {
              label: "Opened",
              key: "opened",
            },
            {
              label: "Clicked",
              key: "clicked",
            },
            {
              label: "Bounced",
              key: "bounced",
            },
          ]}
        />
        {rows.length === 0 &&
          getEmailDeliverabilityInformationStatus !== "loading" &&
          getEmptyCard(
            "No deliverability information found",
            "Try searching for a different time interval"
          )}
      </>
    );
  };
  const getEmailDeliverabilityDropdowns = () => {
    return getDropdownFromRecord(
      {
        allTime: "All Time",
        past7Days: "Past 7 Days",
        past30Days: "Past 30 Days",
        past365Days: "Past 365 Days",
      },
      timeInterval,
      (timeInterval: string | null) => {
        if (!timeInterval) {
          return;
        }
        setTimeInterval(timeInterval);
      },
      getEmailDeliverabilityInformationStatus === "loading"
    );
  };

  const getPeopleWithDeliverabilityIssuesTable = () => {
    const rows = getPeopleWithDeliverabilityTableRows();
    const isLoading =
      getPeopleWithDeliverabilityIssuesStatus === "loading" ||
      displayedInput !== searchInput;

    return (
      <>
        <SortableTable
          rows={rows}
          isLoading={isLoading}
          columnHeaders={[
            {
              label: "Name",
              key: "name",
            },
            {
              label: "Email",
              key: "email",
            },
            {
              label: "Bounce Count",
              key: "bounceCount",
            },
            {
              label: "Last Bounce Date",
              key: "lastBounceDate",
            },
            {
              label: "Last Email Delivered",
              key: "lastEmailDelivered",
            },
          ]}
          itemsPerPage={10}
        />
        {rows.length === 0 &&
          !isLoading &&
          getEmptyCard(
            "No people with deliverability issues found",
            searchInput
              ? "Try searching for a different name"
              : "No one in your company currently has deliverability issues"
          )}
      </>
    );
  };

  const getPeopleWithDeliverabilityIssuesSearch = () => {
    const { selectStyles, components } = getSelectProps();

    return (
      <Form.Group>
        <Select
          placeholder={"Search by name"}
          isClearable={true}
          isSearchable={true}
          components={components}
          inputValue={displayedInput}
          styles={selectStyles}
          menuIsOpen={false}
          onInputChange={(e, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setDisplayedInput(e);
              onSearchInputChange(e);
            }
          }}
        />
      </Form.Group>
    );
  };

  const getEmailDeliverabilityTableRows = () => {
    const rows: RowContent[] = [];
    const emailDeliverabilityInformationForTimeInterval =
      allEmailDeliverabilityInformation[timeInterval];

    if (!emailDeliverabilityInformationForTimeInterval) {
      return rows;
    }

    Object.entries(emailDeliverabilityInformationForTimeInterval).forEach(
      ([emailType, emailDeliverabilityInformation]) => {
        rows.push({
          emailType: {
            displayValue: getEmailTypeDisplayValue(
              emailType,
              emailDeliverabilityInformation.totalEmailsSent,
              emailDeliverabilityInformation.percentageDelivered
            ),
            sortValue: EMAIL_TYPE_TO_LABEL_MAP[emailType],
          },
          totalEmailsSent: {
            displayValue: onFormatLargeNumber(
              emailDeliverabilityInformation.totalEmailsSent
            ),
            sortValue: emailDeliverabilityInformation.totalEmailsSent,
          },
          delivered: {
            displayValue: getPercentageValue(
              emailDeliverabilityInformation.percentageDelivered ?? 0
            ),
            sortValue: emailDeliverabilityInformation.percentageDelivered,
          },
          opened: {
            displayValue: getPercentageValue(
              emailDeliverabilityInformation.percentageOpened
            ),
            sortValue: emailDeliverabilityInformation.percentageOpened,
          },
          clicked: {
            displayValue: getPercentageValue(
              emailDeliverabilityInformation.percentageClicked
            ),
            sortValue: emailDeliverabilityInformation.percentageClicked,
          },
          bounced: {
            displayValue: getPercentageValue(
              emailDeliverabilityInformation.percentageBounced
            ),
            sortValue: emailDeliverabilityInformation.percentageBounced,
          },
        });
      }
    );
    return rows;
  };

  const getEmailTypeDisplayValue = (
    emailType: string,
    totalEmailsSent: number,
    percentageDelivered: number
  ) => {
    let dotColor = "red";
    if (percentageDelivered > 95 || totalEmailsSent === 0) {
      dotColor = "green";
    } else if (percentageDelivered > 80) {
      dotColor = "yellow";
    }
    return (
      <div className="row-gap-8px align-items-center">
        <div className={`status-dot ${dotColor}`} />
        {EMAIL_TYPE_TO_LABEL_MAP[emailType]}
      </div>
    );
  };

  const getDateString = (dateString?: string | null) => {
    // convert 2025-06-02 05:30:19 to 06/02/2025
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getPercentageValue = (percentage?: number | null) => {
    if (!percentage && percentage !== 0) {
      return "-";
    }
    return `${percentage}%`;
  };

  const getPeopleWithDeliverabilityTableRows = () => {
    const rows: RowContent[] = [];
    filteredPeopleWithDeliverabilityIssues.forEach(
      (peopleWithDeliverabilityIssue) => {
        const { userAccountId } = peopleWithDeliverabilityIssue;
        const user = usersById[userAccountId];
        if (!user) {
          return;
        }

        const firstName =
          user?.firstName ?? peopleWithDeliverabilityIssue.firstName ?? "";
        const lastName =
          user?.lastName ?? peopleWithDeliverabilityIssue.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim();
        const nameDisplayValue = (
          <Link to={`/UserGuide/${userAccountId}`}>{fullName}</Link>
        );
        rows.push({
          userAccountId: {
            displayValue: userAccountId,
            sortValue: userAccountId,
          },
          name: {
            displayValue: nameDisplayValue,
            sortValue: fullName,
          },
          email: {
            displayValue: user?.emailAddress,
            sortValue: user?.emailAddress,
          },
          bounceCount: {
            displayValue: onFormatLargeNumber(
              peopleWithDeliverabilityIssue.totalBounces
            ),
            sortValue: peopleWithDeliverabilityIssue.totalBounces,
          },
          lastBounceDate: {
            displayValue: getDateString(
              peopleWithDeliverabilityIssue.mostRecentBounceDate
            ),
            sortValue: peopleWithDeliverabilityIssue.mostRecentBounceDate,
          },
          lastEmailDelivered: {
            displayValue: getDateString(
              peopleWithDeliverabilityIssue.lastDeliveredDate
            ),
            sortValue: peopleWithDeliverabilityIssue.lastDeliveredDate ?? null,
          },
        });
      }
    );
    return rows;
  };

  const peopleWithDeliverabilityIssuesCount = useMemo(() => {
    const filteredPeopleWithDeliverabilityIssues =
      peopleWithDeliverabilityIssues.filter((peopleWithDeliverabilityIssue) => {
        // filter out any users who do not have any bounced emails
        if (!peopleWithDeliverabilityIssue.totalBounces) {
          return false;
        }
        // Or filter out any users who have not bounced in the last 30 days
        const { mostRecentBounceDate } = peopleWithDeliverabilityIssue;
        const date = new Date(mostRecentBounceDate);
        const today = new Date();
        const diffTime = Math.abs(today.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 30;
      });
    return filteredPeopleWithDeliverabilityIssues.length;
  }, [peopleWithDeliverabilityIssues]);

  const getEmptyCard = (title: string, body: string) => (
    <div className="empty-card">
      <div className="column-gap-12px">
        <span>
          <b>{title}</b>
        </span>
        <p>{body}</p>
      </div>
    </div>
  );

  const getCardBody = () => {
    if (tabShowing === "PeopleWithDeliverabilityIssues") {
      return (
        <div className="column-gap-16px">
          <div>
            <h2>
              People with Deliverability Issues{" "}
              {getPeopleWithDeliverabilityIssuesStatus !== "loading"
                ? `(${peopleWithDeliverabilityIssuesCount})`
                : null}
            </h2>
            <p>Review recipients experiencing deliverability issues.</p>
          </div>

          {getPeopleWithDeliverabilityIssuesSearch()}
          {getPeopleWithDeliverabilityIssuesTable()}
        </div>
      );
    }
    return (
      <div className="column-gap-16px">
        <div className="d-flex justify-content-between">
          <div>
            <h2>Email Check In Deliverability</h2>
            <p>
              Monitor email deliverability performance across all sent messages.
            </p>
          </div>
        </div>

        {getEmailDeliverabilityDropdowns()}
        {getEmailDeliverabilityTable()}
      </div>
    );
  };

  return (
    <>
      {getBackButton()}
      <Card className="padding-20px column-gap-24px">
        <h1>Email Performance</h1>
        <Nav
          className="simple-nav"
          activeKey={tabShowing || ""}
          onSelect={(e) => {
            if (!e) return;
            onTabChange?.(e);
          }}
        >
          <Nav.Item>
            <Nav.Link eventKey="EmailDeliverabilityIssues">
              Check-In Email Deliverability
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="PeopleWithDeliverabilityIssues">
              {peopleWithDeliverabilityIssuesCount ? (
                <div>
                  <FontAwesomeIcon
                    icon={["far", "exclamation-triangle"]}
                    className="me-2"
                  />
                  People with Email Issues
                  <span
                    className="label-tag gold"
                    style={{ padding: "6px", borderRadius: "50%" }}
                  >
                    {peopleWithDeliverabilityIssuesCount}
                  </span>
                </div>
              ) : (
                "People with Email Issues"
              )}
            </Nav.Link>
          </Nav.Item>
        </Nav>
        {getCardBody()}
      </Card>
    </>
  );
};

export default EmailCheckInDeliverabilityCard;
